import { API_PATH, BASE_PATH } from './config';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Chip, Button, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions,
    MenuItem, Select, FormControl, InputLabel, Alert,
    IconButton, Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ExpenseManager from './components/ExpenseManager';

const API = `${API_PATH}/expense_reports.php`;

export default function ExpenseReportDetailPage({ user }) {
    const { reportId } = useParams();
    const navigate = useNavigate();
    const userId = user?.pu_id || null;
    const userParam = userId ? `&user_id=${userId}` : '';

    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);

    // Reassign dialog state
    const [reassignOpen, setReassignOpen] = useState(false);
    const [trips, setTrips] = useState([]);
    const [tripsLoading, setTripsLoading] = useState(false);
    const [selectedTripId, setSelectedTripId] = useState('');
    const [reassignSaving, setReassignSaving] = useState(false);
    const [reassignError, setReassignError] = useState('');

    const loadReport = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API}?action=get&id=${reportId}${userParam}&t=${Date.now()}`);
            const data = await res.json();
            setReport(data);
        } finally {
            setLoading(false);
        }
    }, [reportId, userParam]);

    useEffect(() => { loadReport(); }, [loadReport]);

    const openReassign = async () => {
        setReassignError('');
        setSelectedTripId(report?.trip_id ? String(report.trip_id) : '');
        setReassignOpen(true);
        setTripsLoading(true);
        try {
            const res = await fetch(`${API}?action=trips${userParam}`);
            const data = await res.json();
            setTrips(data.trips || []);
        } finally {
            setTripsLoading(false);
        }
    };

    const handleReassign = async () => {
        setReassignSaving(true);
        setReassignError('');
        try {
            const res = await fetch(`${API}?action=assign_trip`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: parseInt(reportId),
                    trip_id: selectedTripId ? parseInt(selectedTripId) : null,
                }),
            });
            const data = await res.json();
            if (!res.ok) {
                setReassignError(data.error || 'Failed to reassign report');
                return;
            }
            setReassignOpen(false);
            await loadReport();
        } finally {
            setReassignSaving(false);
        }
    };

    const isOwner = report && userId && report.owner_pu_id === userId;

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;
    if (!report || report.error) return (
        <Box sx={{ p: 2 }}>
            <Typography color="error">{report?.error || 'Report not found'}</Typography>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/expenses')} sx={{ mt: 2 }}>
                Back to My Expenses
            </Button>
        </Box>
    );

    return (
        <Box sx={{ p: 2 }}>
            {/* Breadcrumb nav */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/expenses')}
                    size="small"
                    sx={{ color: '#fff', flexShrink: 0 }}
                >
                    My Expenses
                </Button>
                <Box sx={{ flex: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {report.trip_name ? (
                        <Chip
                            icon={<FlightTakeoffIcon />}
                            label={report.trip_name}
                            size="small"
                            variant="outlined"
                            onClick={() => navigate(`/trip/${report.trip_id}`)}
                            sx={{ cursor: 'pointer', color: '#fff', borderColor: 'rgba(255,255,255,0.5)', '& .MuiChip-icon': { color: '#fff' } }}
                        />
                    ) : (
                        <Chip label="Unassigned" size="small" variant="outlined" sx={{ color: '#c9d1d9', borderColor: 'rgba(139,148,158,0.4)' }} />
                    )}
                    {isOwner && (
                        <Tooltip title={report.trip_name ? 'Reassign Trip' : 'Assign Trip'}>
                            <IconButton
                                size="small"
                                onClick={openReassign}
                                sx={{ color: '#fff', border: '1px solid rgba(255,255,255,0.45)', borderRadius: '6px', padding: '3px', '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' } }}
                            >
                                <SwapHorizIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </Box>

            {/* The existing ExpenseManager handles all expense CRUD. */}
            <ExpenseManager
                tripId={report.trip_id || null}
                userId={userId}
                reportId={parseInt(reportId)}
                reportTitle={report.title}
                user={user}
            />

            {/* Reassign trip dialog */}
            <Dialog open={reassignOpen} onClose={() => setReassignOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>
                    {report.trip_name ? 'Reassign to a Different Trip' : 'Assign to a Trip'}
                </DialogTitle>
                <DialogContent>
                    {reassignError && <Alert severity="error" sx={{ mb: 2 }}>{reassignError}</Alert>}
                    {report.trip_name && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Currently assigned to: <strong>{report.trip_name}</strong>
                        </Typography>
                    )}
                    {tripsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        <FormControl fullWidth size="small">
                            <InputLabel>Trip</InputLabel>
                            <Select
                                value={selectedTripId}
                                label="Trip"
                                onChange={e => setSelectedTripId(e.target.value)}
                            >
                                <MenuItem value=""><em>— No trip (unassign) —</em></MenuItem>
                                {trips.map(t => (
                                    <MenuItem key={t.trip_id} value={String(t.trip_id)}>
                                        {t.name || `Trip #${t.trip_id}`}
                                        {t.start_date ? ` (${t.start_date.slice(0, 7)})` : ''}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </DialogContent>
                <DialogActions>
                    {report.trip_name && (
                        <Button
                            startIcon={<LinkOffIcon />}
                            color="warning"
                            onClick={() => { setSelectedTripId(''); }}
                            sx={{ mr: 'auto' }}
                        >
                            Unassign
                        </Button>
                    )}
                    <Button onClick={() => setReassignOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleReassign}
                        disabled={reassignSaving || tripsLoading}
                    >
                        {reassignSaving ? <CircularProgress size={18} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
